"use client";

import { useCallback, useEffect, useRef } from "react";
import { driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import "./tours.css";
import { useTours } from "./TourProvider";

const POLL_MS = 300;
const POLL_TIMEOUT_MS = 8000;

// One tour at a time across the whole app — two panels mounting in quick
// succession must never stack overlays. The loser isn't marked seen, so it
// fires on its next visit.
let activeTour: string | null = null;

function visible(selector: string): boolean {
    const el = document.querySelector(selector);
    return !!el && (el as HTMLElement).offsetParent !== null;
}

/** Steps whose anchor is missing or hidden are dropped so driver.js never
 * shows a mispositioned popover (e.g. `#row0` on an empty table). */
function presentSteps(steps: DriveStep[]): DriveStep[] {
    return steps.filter((s) => typeof s.element === "string" && visible(s.element));
}

/** The exit hint + opt-out row driver.js has no config for, appended once per
 * popover render. Rebuilt each step because driver.js reuses one footer node. */
function decorateFooter(footer: HTMLElement, onOptOut: () => void, onClose: () => void) {
    footer.querySelector(".cmt-tour-exit")?.remove();

    const row = document.createElement("div");
    row.className = "cmt-tour-exit";

    const hint = document.createElement("p");
    hint.className = "cmt-tour-exit-hint";
    hint.innerHTML = "Click anywhere outside or press <kbd>Esc</kbd> to exit.";

    const optOut = document.createElement("label");
    optOut.className = "cmt-tour-optout";
    const box = document.createElement("input");
    box.type = "checkbox";
    box.addEventListener("change", () => {
        if (!box.checked) return;
        onOptOut();
        onClose();
    });
    optOut.append(box, document.createTextNode("Don't show me tours"));

    row.append(hint, optOut);
    footer.appendChild(row);
}

function launch(
    tourId: string,
    steps: DriveStep[],
    opts?: { onDone?: () => void; onOptOut?: () => void }
): boolean {
    const runnable = presentSteps(steps);
    if (!runnable.length || activeTour) return false;
    activeTour = tourId;
    const d = driver({
        showProgress: true,
        progressText: "{{current}} of {{total}}",
        popoverClass: "cmt-tour",
        showButtons: ["next", "previous", "close"],
        steps: runnable,
        nextBtnText: "Next →",
        prevBtnText: "← Back",
        doneBtnText: "Done",
        onPopoverRender: (popover) => {
            if (!popover?.footer) return;
            decorateFooter(
                popover.footer,
                () => opts?.onOptOut?.(),
                () => d.destroy()
            );
        },
        onDestroyed: () => {
            activeTour = null;
            opts?.onDone?.();
        },
    });
    d.drive();
    return true;
}

/**
 * Auto-launches a guided tour the first time the current user reaches this
 * screen, and returns a `replay` that re-runs it on demand (ignores seen-state).
 *
 * Dismissing counts as seen — the per-screen "Show me around" button is the
 * recovery path. Waits for the first anchor to mount (panels load data async);
 * gives up silently, without marking seen, if it never appears.
 *
 * A user who ticked "Don't show me tours" is never auto-toured again; `replay`
 * ignores both that flag and seen-state, so the button always works.
 */
export function useAutoTour(
    tourId: string,
    steps: DriveStep[],
    opts?: { enabled?: boolean }
) {
    const { isLoaded, seen, hasSeen, markSeen, suppressed, suppressAll } = useTours();
    const stepsRef = useRef(steps);
    useEffect(() => {
        stepsRef.current = steps;
    });
    const enabled = opts?.enabled !== false;

    // Keep the opt-out callback in a ref: it must be current inside the
    // popover's change handler without re-arming the auto-run effect.
    const suppressRef = useRef(suppressAll);
    useEffect(() => {
        suppressRef.current = suppressAll;
    });

    useEffect(() => {
        if (!enabled || !isLoaded || seen === null || suppressed || hasSeen(tourId)) return;

        let cancelled = false;
        let waited = 0;
        const tryStart = () => {
            if (cancelled) return;
            // Another tour won the session — skip; this one fires on the next
            // visit since it was never marked seen.
            if (activeTour) return;
            // Hold off while a blocking modal (orientation note, CoC gate) is up.
            const blocked = !!document.querySelector("[data-tour-blocking-modal]");
            const first = stepsRef.current[0];
            const anchorReady = !!first && typeof first.element === "string" && visible(first.element);
            if (!blocked && anchorReady) {
                const started = launch(tourId, stepsRef.current, {
                    onDone: () => markSeen(tourId),
                    onOptOut: () => suppressRef.current(),
                });
                if (started) return;
            }
            waited += POLL_MS;
            if (waited < POLL_TIMEOUT_MS) setTimeout(tryStart, POLL_MS);
        };
        const t = setTimeout(tryStart, POLL_MS);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
        // Re-evaluate only when the screen mounts or auth/seen state resolves —
        // deliberately not on every steps identity change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tourId, enabled, isLoaded, seen === null, suppressed]);

    const replay = useCallback(() => {
        launch(tourId, stepsRef.current, { onOptOut: () => suppressRef.current() });
    }, [tourId]);

    return { replay };
}
